import { z } from 'zod';
import { RARITIES, type Rarity } from '../rarity';

/**
 * **As criaturas aquáticas do mundo aberto: vinte e sete, por posição.**
 *
 * ## A posição é a progressão, e é a identidade dela
 *
 * Cada espécie tem uma `position` de 1 a 27, e ela **não é decorativa**: é a
 * ordem canónica da dificuldade e do valor. A raridade é uma consequência da
 * posição — a banda em que ela cai — e não o contrário. Uma espécie nova obriga
 * a decidir onde ela entra, e a asserção abaixo recusa uma posição repetida, uma
 * lacuna e uma que caia fora da banda da sua raridade.
 *
 * ## Duas ou três por raridade, e o compilador escolhe qual
 *
 * A distribuição aprovada dá **três** espécies às sete primeiras raridades e
 * **duas** às três últimas. A tabela é um `Record<Rarity, …>` sobre uma união de
 * duas tuplas: uma raridade em falta não compila, uma com uma só não compila, e
 * uma com quatro também não. "Duas ou três" deixou de ser uma convenção que
 * alguém tem de lembrar.
 *
 * Até ao gate V28I.BK eram exatamente duas, e a tupla dizia-o. Vinte espécies
 * passaram a vinte e sete, e as vinte antigas **não desapareceram**: elas vivem
 * em `RETIRED_FISH_SPECIES`, continuam a ser reconhecidas, nomeadas e pagas, e
 * nunca mais são sorteadas.
 *
 * ## O que uma espécie decide, e o que não
 *
 * Ela decide a identidade, a posição, o nome, a raridade, a vida-base e a faixa
 * de peso possível. Não decide a **frequência**: com que probabilidade cada
 * raridade sai é uma propriedade da água, não do peixe, e vive na tabela de
 * equilíbrio. Nem decide a vida final, que depende do peso individual daquela
 * captura.
 *
 * ## Os ids são para sempre
 *
 * Os identificadores são técnicos e estáveis; os nomes visíveis são português e
 * podem mudar. O que não pode mudar sem uma decisão é o **id**: ele atravessa o
 * selo, a carga de campo, a caixa de morte, a extração e `account_catches`, e é
 * a identidade da espécie para sempre.
 */

/** Uma espécie, como o catálogo a define. */
export interface FishSpecies {
  /**
   * **A posição canónica na progressão**, de 1 a 27.
   *
   * Ela ordena a dificuldade e o valor, decide a banda de raridade, e é a chave
   * das provas de arranque. Uma espécie retirada não tem posição — ver
   * {@link RETIRED_FISH_SPECIES}.
   */
  readonly position: number;
  /** Estável, e a identidade para sempre. Atravessa selo, carga e cofre. */
  readonly id: string;
  /** O nome que o jogador vê. */
  readonly displayName: string;
  readonly rarity: Rarity;
  /**
   * **O peso de referência**, em miligramas.
   *
   * O denominador do fator de peso: um exemplar exatamente com este peso tem a
   * vida-base vezes o multiplicador da raridade, sem mais nada. É por isso que
   * ele é uma propriedade da ESPÉCIE — uma baleia de referência e uma larva de
   * referência não pesam o mesmo, e o fator tem de significar a mesma coisa nas
   * duas.
   */
  readonly referenceWeightMg: number;
  /** O menor e o maior exemplar possível, em miligramas. Inclusive. */
  readonly minWeightMg: number;
  readonly maxWeightMg: number;
  /**
   * A vida de um exemplar de referência, em **milli-unidades de vida** — a
   * mesma unidade em que a simulação mede tudo o resto.
   */
  readonly baseHealthMilli: bigint;
}

/** Um grama, em miligramas. A unidade das faixas abaixo, escrita uma vez. */
const G = 1_000;
/** Um quilograma, em miligramas. */
const KG = 1_000_000;

function species(
  position: number,
  id: string,
  displayName: string,
  rarity: Rarity,
  weights: { readonly min: number; readonly reference: number; readonly max: number },
  baseHealthMilli: bigint,
): FishSpecies {
  return Object.freeze({
    position,
    id,
    displayName,
    rarity,
    referenceWeightMg: weights.reference,
    minWeightMg: weights.min,
    maxWeightMg: weights.max,
    baseHealthMilli,
  });
}

/**
 * **As bandas de posição de cada raridade**, e a distribuição aprovada.
 *
 * Escritas uma vez, e lidas pela prova de arranque: a tabela abaixo não pode
 * pôr uma espécie numa raridade cuja banda não contenha a posição dela. Sem
 * isto, "a numeração determina a progressão de raridade" seria uma frase num
 * comentário em vez de uma coisa verificada.
 */
export const FISH_RARITY_POSITIONS: Readonly<Record<Rarity, readonly [number, number]>> =
  Object.freeze({
    common: [1, 3],
    uncommon: [4, 6],
    rare: [7, 9],
    epic: [10, 12],
    mythic: [13, 15],
    legendary: [16, 18],
    excellent: [19, 21],
    perfect: [22, 23],
    anomaly: [24, 25],
    prismatic: [26, 27],
  });

/** Quantas espécies ativas existem. Vinte e sete, e a prova abaixo exige-o. */
export const FISH_SPECIES_COUNT = 27;

/** Um par de espécies, ou um trio. Nunca uma, e nunca quatro. */
export type FishSpeciesGroup =
  | readonly [FishSpecies, FishSpecies]
  | readonly [FishSpecies, FishSpecies, FishSpecies];

/**
 * **O catálogo, total por construção.**
 *
 * As vidas-base e as faixas de peso são a **curva inicial aprovada** no gate
 * V28I.BJ, sujeita à validação final do projeto. Elas foram construídas de trás
 * para a frente: no peso de referência o fator `peso / pesoReferência` vale um,
 * portanto a vida e o preço de referência dependem **apenas** da vida-base, do
 * valor-base e da raridade. A curva de referência é uma progressão geométrica
 * de 27 passos entre os extremos que a curva B já tinha, e as vidas-base caem
 * dela.
 *
 * **Os quatro pesos dos megalodontes são a excepção deliberada.** Um filhote
 * pesa menos do que o adulto da mesma linhagem, embora esteja numa posição e
 * numa raridade superiores: a força-base e a raridade são parcelas independentes
 * do peso na fórmula, portanto um filhote mais leve continua a ser mais forte e
 * mais valioso. O peso diz o tamanho; a posição diz a dificuldade.
 */
export const FISH_SPECIES: Readonly<Record<Rarity, FishSpeciesGroup>> = Object.freeze({
  common: [
    species(
      1,
      'fish_tambaqui',
      'Tambaqui',
      'common',
      { min: 21 * G, reference: 60 * G, max: 160 * G },
      900n,
    ),
    species(
      2,
      'fish_pirarucu',
      'Pirarucu',
      'common',
      { min: 32 * G, reference: 92 * G, max: 240 * G },
      1_300n,
    ),
    species(
      3,
      'fish_baiacu',
      'Baiacu',
      'common',
      { min: 49 * G, reference: 140 * G, max: 360 * G },
      2_000n,
    ),
  ],
  uncommon: [
    species(
      4,
      'fish_space_frog',
      'Sapo Espacial',
      'uncommon',
      { min: 74 * G, reference: 210 * G, max: 550 * G },
      2_400n,
    ),
    species(
      5,
      'fish_all_seeing_serpent',
      'Serpente Onividente',
      'uncommon',
      { min: 120 * G, reference: 330 * G, max: 860 * G },
      3_600n,
    ),
    species(
      6,
      'fish_rainbow',
      'Peixe-Arco-Íris',
      'uncommon',
      { min: 180 * G, reference: 500 * G, max: 1300 * G },
      5_400n,
    ),
  ],
  rare: [
    species(
      7,
      'fish_angel',
      'Peixe-Anjo',
      'rare',
      { min: 270 * G, reference: 760 * G, max: 2 * KG },
      6_400n,
    ),
    species(
      8,
      'fish_monster',
      'Peixe-Monstro',
      'rare',
      { min: 420 * G, reference: 1200 * G, max: 3100 * G },
      9_400n,
    ),
    species(
      9,
      'fish_fire',
      'Peixe-Fogo',
      'rare',
      { min: 630 * G, reference: 1800 * G, max: 4700 * G },
      14_000n,
    ),
  ],
  epic: [
    species(
      10,
      'fish_strange',
      'Peixe-Estranho',
      'epic',
      { min: 940 * G, reference: 2700 * G, max: 7 * KG },
      16_000n,
    ),
    species(
      11,
      'fish_eel',
      'Enguia',
      'epic',
      { min: 1400 * G, reference: 4 * KG, max: 10400 * G },
      24_000n,
    ),
    species(
      12,
      'fish_demon',
      'Peixe-Demônio',
      'epic',
      { min: 2100 * G, reference: 6100 * G, max: 15900 * G },
      36_000n,
    ),
  ],
  mythic: [
    species(
      13,
      'fish_abyssal_chimera',
      'Quimera Abissal',
      'mythic',
      { min: 3200 * G, reference: 9200 * G, max: 23900 * G },
      42_000n,
    ),
    species(
      14,
      'fish_whale',
      'Baleia',
      'mythic',
      { min: 4900 * G, reference: 14 * KG, max: 36400 * G },
      62_000n,
    ),
    species(
      15,
      'fish_mosasaur',
      'Mosassauro',
      'mythic',
      { min: 7400 * G, reference: 21 * KG, max: 54600 * G },
      92_000n,
    ),
  ],
  legendary: [
    species(
      16,
      'fish_titanoboa',
      'Titanoboa',
      'legendary',
      { min: 12 * KG, reference: 34 * KG, max: 88 * KG },
      110_000n,
    ),
    species(
      17,
      'fish_baphomet',
      'Bafomé Abissal',
      'legendary',
      { min: 18 * KG, reference: 52 * KG, max: 135 * KG },
      160_000n,
    ),
    species(
      18,
      'fish_megalodon',
      'Megalodonte',
      'legendary',
      { min: 42 * KG, reference: 120 * KG, max: 312 * KG },
      240_000n,
    ),
  ],
  excellent: [
    species(
      19,
      'fish_baby_megalodon',
      'Megalodonte Filhote',
      'excellent',
      { min: 28 * KG, reference: 80 * KG, max: 208 * KG },
      280_000n,
    ),
    species(
      20,
      'fish_golden_megalodon',
      'Megalodonte Dourado',
      'excellent',
      { min: 98 * KG, reference: 280 * KG, max: 728 * KG },
      420_000n,
    ),
    species(
      21,
      'fish_golden_baby_megalodon',
      'Megalodonte Filhote Dourado',
      'excellent',
      { min: 66 * KG, reference: 190 * KG, max: 494 * KG },
      620_000n,
    ),
  ],
  perfect: [
    species(
      22,
      'fish_black_megalodon',
      'Megalodonte Negro',
      'perfect',
      { min: 150 * KG, reference: 430 * KG, max: 1100 * KG },
      720_000n,
    ),
    species(
      23,
      'fish_colossal_squid',
      'Lula Colossal',
      'perfect',
      { min: 230 * KG, reference: 660 * KG, max: 1700 * KG },
      1_100_000n,
    ),
  ],
  anomaly: [
    species(
      24,
      'fish_kraken',
      'Kraken',
      'anomaly',
      { min: 350 * KG, reference: 1000 * KG, max: 2600 * KG },
      1_200_000n,
    ),
    species(
      25,
      'fish_devil_dragon',
      'Dragão Demônio',
      'anomaly',
      { min: 520 * KG, reference: 1500 * KG, max: 3900 * KG },
      1_800_000n,
    ),
  ],
  prismatic: [
    species(
      26,
      'fish_abyssal_titanic',
      'Titanic Abissal',
      'prismatic',
      { min: 840 * KG, reference: 2400 * KG, max: 6200 * KG },
      2_100_000n,
    ),
    species(
      27,
      'fish_lord_poseidon',
      'Lorde Poseidon',
      'prismatic',
      { min: 1300 * KG, reference: 3600 * KG, max: 9400 * KG },
      3_100_000n,
    ),
  ],
});

/** Todas as espécies ativas, achatadas, na ordem canónica das raridades. */
export const FISH_SPECIES_LIST: readonly FishSpecies[] = Object.freeze(
  RARITIES.flatMap((rarity) => [...FISH_SPECIES[rarity]]),
);

/* -------------------------------------------------------------------------- */
/*  As espécies retiradas                                                     */
/* -------------------------------------------------------------------------- */

/**
 * **As vinte que saíram do sorteio no gate V28I.BK, e não do jogo.**
 *
 * Uma captura é **propriedade de um jogador**. Ela está em `account_catches`, ou
 * numa carga de campo, ou numa caixa pública, ou numa instantânea de uma sala —
 * e nenhuma dessas linhas pode ser apagada, remapeada ou desvalorizada porque o
 * catálogo mudou. Antes deste ficheiro existir, um id que saísse do catálogo
 * valia `FISH_PRICE_FLOOR` — **um Ouro** — e mostrava-se pelo id cru. Uma Baleia
 * Gravítica de referência valia 5 440 Ouro; teria passado a valer 1.
 *
 * É o mesmo padrão que `RETIRED_PET_IDS` já usa para conteúdo retirado, com os
 * números que um preço precisa por cima: elas mantêm nome, raridade, pesos,
 * vida-base e valor-base **exatamente** como os tinham.
 *
 * ## O que elas são, e o que deixaram de ser
 *
 * - continuam reconhecidas por `findFishSpecies` e por `isFishSpeciesId`;
 * - continuam a valer o que valiam, e continuam vendáveis;
 * - continuam restauráveis de uma instantânea;
 * - **nunca voltam a ser sorteadas**: `FISH_SPECIES_LIST`, que é o pool, não as
 *   contém;
 * - **não entram na progressão 01–27**, e por isso não têm `position`;
 * - **não são remapeadas** para nenhuma das 27 novas. Uma Baleia Gravítica é uma
 *   Baleia Gravítica, e nunca se transforma noutra criatura;
 * - **não têm arte, e não vão inventar uma.** Elas nunca tiveram; a superfície
 *   mostra o recuo honesto de imagem indisponível, com o nome certo.
 */

/**
 * Uma espécie retirada. A mesma forma de uma ativa, **menos a posição**.
 *
 * `Omit` e não uma interface própria: elas são a mesma coisa em tudo menos no
 * facto de já não estarem na progressão, e duas formas independentes seriam duas
 * a divergir no dia em que uma espécie ganhasse um campo.
 */
export type RetiredFishSpecies = Omit<FishSpecies, 'position'>;

function retired(
  id: string,
  displayName: string,
  rarity: Rarity,
  weights: { readonly min: number; readonly reference: number; readonly max: number },
  baseHealthMilli: bigint,
): RetiredFishSpecies {
  return Object.freeze({
    id,
    displayName,
    rarity,
    referenceWeightMg: weights.reference,
    minWeightMg: weights.min,
    maxWeightMg: weights.max,
    baseHealthMilli,
  });
}

export const RETIRED_FISH_SPECIES: readonly RetiredFishSpecies[] = Object.freeze([
  retired(
    'fish_silt_larva',
    'Larva de Sedimento',
    'common',
    { min: 20 * G, reference: 60 * G, max: 140 * G },
    900n,
  ),
  retired(
    'fish_glass_minnow',
    'Peixe-Vidro',
    'common',
    { min: 30 * G, reference: 80 * G, max: 180 * G },
    1_100n,
  ),
  retired(
    'fish_ion_eel',
    'Enguia Iónica',
    'uncommon',
    { min: 200 * G, reference: 500 * G, max: 1_200 * G },
    2_400n,
  ),
  retired(
    'fish_dust_ray',
    'Raia de Poeira',
    'uncommon',
    { min: 300 * G, reference: 700 * G, max: 1_600 * G },
    2_800n,
  ),
  retired(
    'fish_tidal_lantern',
    'Lanterna de Maré',
    'rare',
    { min: 1 * KG, reference: 3 * KG, max: 8 * KG },
    6_200n,
  ),
  retired(
    'fish_hollow_carp',
    'Carpa Oca',
    'rare',
    { min: 2 * KG, reference: 4 * KG, max: 10 * KG },
    7_000n,
  ),
  retired(
    'fish_ferrite_moray',
    'Moreia de Ferrite',
    'epic',
    { min: 5 * KG, reference: 12 * KG, max: 30 * KG },
    15_000n,
  ),
  retired(
    'fish_pressure_jaw',
    'Mandíbula de Pressão',
    'epic',
    { min: 7 * KG, reference: 16 * KG, max: 38 * KG },
    17_500n,
  ),
  retired(
    'fish_void_angler',
    'Tamboril do Vazio',
    'mythic',
    { min: 15 * KG, reference: 40 * KG, max: 90 * KG },
    36_000n,
  ),
  retired(
    'fish_nebula_koi',
    'Carpa Nebulosa',
    'mythic',
    { min: 18 * KG, reference: 45 * KG, max: 100 * KG },
    40_000n,
  ),
  retired(
    'fish_gravity_whale',
    'Baleia Gravítica',
    'legendary',
    { min: 60 * KG, reference: 150 * KG, max: 400 * KG },
    88_000n,
  ),
  retired(
    'fish_ember_serpent',
    'Serpente de Brasa',
    'legendary',
    { min: 50 * KG, reference: 130 * KG, max: 360 * KG },
    95_000n,
  ),
  retired(
    'fish_chrome_marlin',
    'Espadarte de Crómio',
    'excellent',
    { min: 120 * KG, reference: 300 * KG, max: 800 * KG },
    210_000n,
  ),
  retired(
    'fish_singing_medusa',
    'Medusa Cantante',
    'excellent',
    { min: 90 * KG, reference: 260 * KG, max: 720 * KG },
    225_000n,
  ),
  retired(
    'fish_mirror_seraph',
    'Serafim-Espelho',
    'perfect',
    { min: 250 * KG, reference: 600 * KG, max: 1_500 * KG },
    480_000n,
  ),
  retired(
    'fish_quantum_lamprey',
    'Lampreia Quântica',
    'perfect',
    { min: 220 * KG, reference: 560 * KG, max: 1_400 * KG },
    510_000n,
  ),
  retired(
    'fish_null_kraken',
    'Kraken Nulo',
    'anomaly',
    { min: 600 * KG, reference: 1_500 * KG, max: 4_000 * KG },
    1_150_000n,
  ),
  retired(
    'fish_paradox_sting',
    'Ferrão Paradoxo',
    'anomaly',
    { min: 500 * KG, reference: 1_300 * KG, max: 3_600 * KG },
    1_240_000n,
  ),
  retired(
    'fish_prism_colossus',
    'Colosso Prismático',
    'prismatic',
    { min: 1_500 * KG, reference: 4_000 * KG, max: 12_000 * KG },
    2_900_000n,
  ),
  retired(
    'fish_aurora_sovereign',
    'Soberano da Aurora',
    'prismatic',
    { min: 1_200 * KG, reference: 3_600 * KG, max: 10_000 * KG },
    3_100_000n,
  ),
]);

/* -------------------------------------------------------------------------- */
/*  A leitura                                                                 */
/* -------------------------------------------------------------------------- */

const ACTIVE_BY_ID: ReadonlyMap<string, FishSpecies> = new Map(
  FISH_SPECIES_LIST.map((entry) => [entry.id, entry]),
);

const RETIRED_BY_ID: ReadonlyMap<string, RetiredFishSpecies> = new Map(
  RETIRED_FISH_SPECIES.map((entry) => [entry.id, entry]),
);

/**
 * **Uma espécie pelo id — ativa ou retirada — ou `null`.**
 *
 * As ativas primeiro, as retiradas depois, e um id desconhecido devolve `null`.
 * Os três casos são distintos e nenhum se confunde com outro: uma retirada tem
 * nome e números, e um id que ninguém conhece não tem nada.
 *
 * O tipo devolvido é `RetiredFishSpecies` — a parte que as duas partilham —
 * porque quem pergunta "qual é esta espécie" quer o nome, a raridade e os
 * números, e **não** a posição. Quem precisa da posição está a falar da
 * progressão, e a progressão só tem ativas: `findActiveFishSpecies`.
 */
export function findFishSpecies(id: string): RetiredFishSpecies | null {
  return ACTIVE_BY_ID.get(id) ?? RETIRED_BY_ID.get(id) ?? null;
}

/** Só as 27 ativas. O pool do sorteio, e quem precisa da posição. */
export function findActiveFishSpecies(id: string): FishSpecies | null {
  return ACTIVE_BY_ID.get(id) ?? null;
}

/** Verdadeiro para uma espécie ativa ou retirada. Falso para um id inventado. */
export function isFishSpeciesId(id: string): boolean {
  return ACTIVE_BY_ID.has(id) || RETIRED_BY_ID.has(id);
}

/** Verdadeiro só para uma das vinte retiradas. */
export function isRetiredFishSpeciesId(id: string): boolean {
  return RETIRED_BY_ID.has(id);
}

/* -------------------------------------------------------------------------- */
/*  As provas, ao carregar                                                    */
/* -------------------------------------------------------------------------- */

/**
 * **O que o compilador não consegue dizer, dito no arranque.**
 *
 * A tabela é total sobre `Rarity` e cada grupo é uma tupla de dois ou três — o
 * compilador garante isso. O resto é aritmética sobre os dados, e falha aqui,
 * onde uma tabela mal editada tem de falhar: no arranque, e não na primeira
 * pescaria de um jogador.
 *
 * A monotonia da vida por posição vive aqui; a do preço vive em
 * `fish-price.ts`, junto da tabela que a produz.
 */
function assertFishCatalogueIsSound(): void {
  if (FISH_SPECIES_LIST.length !== FISH_SPECIES_COUNT) {
    throw new RangeError(
      `the fish catalogue must hold ${FISH_SPECIES_COUNT} active species, found ${FISH_SPECIES_LIST.length}`,
    );
  }

  /* Ids únicos, e nenhum colide com uma retirada. */
  const ids = new Set<string>();
  for (const one of FISH_SPECIES_LIST) {
    if (ids.has(one.id)) {
      throw new RangeError(`fish species ${one.id} appears twice`);
    }
    if (RETIRED_BY_ID.has(one.id)) {
      throw new RangeError(`fish species ${one.id} is both active and retired`);
    }
    ids.add(one.id);
  }

  /* As posições são exatamente 1..27, sem lacunas e sem duplicados. */
  const seen = new Set<number>();
  for (const one of FISH_SPECIES_LIST) {
    if (
      !Number.isSafeInteger(one.position) ||
      one.position < 1 ||
      one.position > FISH_SPECIES_COUNT
    ) {
      throw new RangeError(`fish species ${one.id} has position ${one.position}`);
    }
    if (seen.has(one.position)) {
      throw new RangeError(`two fish species share position ${one.position}`);
    }
    seen.add(one.position);
    /* E cada uma cai na banda da raridade que a distribuição aprovou. */
    const [first, last] = FISH_RARITY_POSITIONS[one.rarity];
    if (one.position < first || one.position > last) {
      throw new RangeError(
        `fish species ${one.id} is ${one.rarity}, whose positions are ${first}..${last}, but sits at ${one.position}`,
      );
    }
  }

  /*
   * **A força-base cresce estritamente com a posição.**
   *
   * É a metade da monotonia que este ficheiro pode provar sozinho. A outra —
   * que o multiplicador de raridade não decresce ao longo das posições — é uma
   * propriedade da tabela de equilíbrio, que vive noutro pacote e prova a vida
   * de referência lá, ao lado dos números dela. Juntas, as duas dão "uma
   * posição maior nunca é mais fraca".
   */
  const byPosition = [...FISH_SPECIES_LIST].sort((a, b) => a.position - b.position);
  let previous = 0n;
  for (const one of byPosition) {
    if (one.baseHealthMilli <= previous) {
      throw new RangeError(
        `fish base health must grow strictly by position; ${one.id} at ${one.position} is ${one.baseHealthMilli} after ${previous}`,
      );
    }
    previous = one.baseHealthMilli;
    if (one.minWeightMg > one.referenceWeightMg || one.referenceWeightMg > one.maxWeightMg) {
      throw new RangeError(`fish species ${one.id} has a reference weight outside its own range`);
    }
  }
}

assertFishCatalogueIsSound();

/* -------------------------------------------------------------------------- */
/*  A vida de um exemplar                                                     */
/* -------------------------------------------------------------------------- */

/** O denominador dos multiplicadores de raridade. Dez mil é um. */
export const FISH_RARITY_DENOMINATOR_BPS = 10_000n;

/**
 * **A vida de UM exemplar**, exata e em inteiros.
 *
 * ```
 *            vida-base × multiplicador da raridade × peso
 * vida  =  ───────────────────────────────────────────────
 *                     10 000 × peso de referência
 * ```
 *
 * Multiplica **tudo** primeiro e divide **uma vez**, portanto trunca uma vez só
 * — dividir em passos truncaria em cada um, e um peixe pesado perderia a
 * diferença que o peso dele devia valer.
 *
 * As três propriedades que o contrato exige saem daqui por construção:
 *
 * - **mais pesado nunca vale menos**, na mesma espécie, porque o peso é um
 *   fator do numerador e a divisão é monótona;
 * - **mais raro aguenta mais**, porque o multiplicador cresce com a raridade e
 *   as vidas-base também;
 * - **nunca zero**: o piso é um milli, e um exemplar minúsculo de uma espécie
 *   comum continua a ser uma batalha e não um clique.
 *
 * Nenhum ponto flutuante decide nada: tudo é `bigint`.
 */
export function fishHealthMilli(input: {
  /*
   * **Ativa ou retirada.** `RetiredFishSpecies` é a parte que as duas
   * partilham, e é tudo o que uma vida precisa: a força-base e o peso de
   * referência. Um exemplar de uma espécie retirada, restaurado de uma
   * instantânea, continua a ter a vida que sempre teve.
   */
  readonly species: RetiredFishSpecies;
  readonly weightMg: number;
  readonly rarityMultiplierBps: number;
}): bigint {
  const numerator =
    input.species.baseHealthMilli *
    BigInt(input.rarityMultiplierBps) *
    BigInt(Math.max(1, Math.trunc(input.weightMg)));
  const denominator = FISH_RARITY_DENOMINATOR_BPS * BigInt(input.species.referenceWeightMg);
  const health = numerator / denominator;
  return health > 0n ? health : 1n;
}

/* -------------------------------------------------------------------------- */
/*  A forma pública                                                           */
/* -------------------------------------------------------------------------- */

/**
 * **O peixe revelado**, e só depois da mordida.
 *
 * Antes dela nada disto viaja: nem espécie, nem raridade, nem peso, nem vida.
 * Cancelar antes da mordida não serve para escolher prémio porque não há nada
 * para ver.
 */
export const openWorldFishRevealSchema = z
  .object({
    /** A vaga do peixe naquela água. Estável enquanto ele existir. */
    i: z.string().min(1).max(64),
    /** O id da espécie. O cliente resolve o nome e a raridade pelo catálogo. */
    s: z.string().min(1).max(64),
    /** O peso deste exemplar, em miligramas. */
    w: z.number().int().min(1),
    /** A vida máxima, em milli, como decimal. */
    hm: z.string().min(1).max(40),
  })
  .strict();

export type OpenWorldFishReveal = z.infer<typeof openWorldFishRevealSchema>;
